import PostsList from 'components/PostsList';
import { Empty, EmptyDescription, EmptyHeader } from 'components/ui/empty';
import { Skeleton } from 'components/ui/skeleton';
import posthog from 'posthog-js';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AskMidwifeCard from './AskMidwifeCard';
import CategoryRows from './CategoryRows';
import { useCategorySearch, usePostSearch } from './queries';

/**
 * Search on the categorised Infos page: the matching categories first (a
 * theme with a single article stays findable through its name and synonyms),
 * then the matching articles.
 */
export default function InfoSearchResults({ query }: { query: string }) {
  const { t } = useTranslation();

  const { data: categories, isPending: loadingCategories } = useCategorySearch(query);
  const { data: posts, isPending: loadingPosts } = usePostSearch(query);
  useEffect(() => {
    if (categories && posts) {
      posthog.capture('info.search_performed', {
        search_query: query,
        search_results_count: posts.length,
        search_category_results_count: categories.length,
      });
    }
  }, [query, categories, posts]);

  if (loadingCategories || loadingPosts) {
    return (
      <div className="flex flex-col gap-5">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  const categoryHits = categories ?? [];
  const postHits = posts ?? [];
  const nothing = categoryHits.length === 0 && postHits.length === 0;
  const areaOf = new Map(categoryHits.map(hit => [hit.id, hit.area?.name]));

  return (
    <div className="flex flex-col gap-5" data-testid="info-search-results">
      {categoryHits.length > 0 && (
        <section className="flex flex-col gap-2">
          <h4>{t('infoPage.structure.topics')}</h4>
          <CategoryRows
            categories={categoryHits}
            openedFrom="info-search"
            getSubline={hit =>
              areaOf.get(hit.id) ? t('infoPage.structure.inArea', { area: areaOf.get(hit.id) }) : undefined
            }
            testIdPrefix="search-category"
          />
        </section>
      )}
      {postHits.length > 0 && (
        <section className="flex flex-col gap-2">
          <h4>{t('infoPage.structure.articlesTitle')}</h4>
          <PostsList
            posts={postHits}
            posthogOnArticleOpenExtras={{ opened_on_page: 'info-search', found_using_search: query }}
            testIdPrefix="postinlist"
          />
        </section>
      )}
      {nothing && (
        <Empty>
          <EmptyHeader>
            <EmptyDescription>{t('infoPage.structure.noResults')}</EmptyDescription>
          </EmptyHeader>
        </Empty>
      )}
      <AskMidwifeCard source="info-search" />
    </div>
  );
}
